import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogoImage } from "@/components/Brand";
import { Loader2, UsersRound } from "lucide-react";

/** v13 · /unirse?token=… — entrar en el equipo de una cuenta madre (plan Business). */
export default function JoinTeam() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("idle"); // idle | joining | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || user === null) return; // sin token o aún comprobando la sesión
    if (!user) {
      // Sin sesión: guardamos la invitación y se acepta sola al entrar o registrarse.
      try { localStorage.setItem("ca_team_invite", token); } catch { /* nada */ }
      return;
    }
    setState("joining");
    api.post("/team/accept", { token })
      .then((r) => {
        try { localStorage.removeItem("ca_team_invite"); } catch { /* nada */ }
        toast.success(`Ya formas parte del equipo de ${r.data.owner?.name || r.data.owner?.email}.`);
        window.location.href = "/app/hoy";
      })
      .catch((e) => { setError(apiError(e)); setState("error"); });
  }, [token, user]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-brand-canvas px-6 py-12">
      <div className="w-full max-w-sm" data-testid="join-team">
        <Link to="/" className="mb-8 block"><LogoImage size="lg" /></Link>
        <div className="flex items-center gap-2 text-brand-accent"><UsersRound size={20} /></div>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-brand-ink" style={{ letterSpacing: "-0.02em" }}>Únete al equipo</h1>
        <p className="mt-2 text-sm text-brand-muted">
          Te han invitado a un equipo de CaptAcción Business. Tendrás tu propia cuenta y prospectarás para el negocio
          de la cuenta madre, sin repetir contactos con el resto del equipo.
        </p>

        {!token ? (
          <div className="mt-8 rounded-md border border-brand-neg/30 bg-brand-neg/[0.06] p-4 text-sm text-brand-neg" data-testid="join-notoken">
            Falta el enlace de invitación. Pide a quien te invitó que te lo envíe de nuevo.
          </div>
        ) : state === "joining" || user === null ? (
          <p className="mt-8 flex items-center gap-2 text-sm text-brand-muted" data-testid="join-loading">
            <Loader2 size={16} className="animate-spin" /> Uniéndote al equipo…
          </p>
        ) : state === "error" ? (
          <div className="mt-8 space-y-4">
            <div className="rounded-md border border-brand-neg/30 bg-brand-neg/[0.06] p-4 text-sm text-brand-neg" data-testid="join-error">{error}</div>
            <button type="button" onClick={() => navigate("/app/hoy")} className="w-full rounded-md border border-brand-line px-4 py-2.5 text-sm font-bold text-brand-ink hover:bg-brand-panel2">
              Ir a mi cuenta
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="join-anon">
            <p className="text-sm text-brand-ink">Usa el mismo correo al que llegó la invitación.</p>
            <Link to="/registro" data-testid="join-register" className="flex w-full items-center justify-center rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas hover:brightness-110">
              Crear mi cuenta
            </Link>
            <Link to="/acceso" data-testid="join-login" className="flex w-full items-center justify-center rounded-md border border-brand-line px-4 py-2.5 text-sm font-bold text-brand-ink hover:bg-brand-panel2">
              Ya tengo cuenta: entrar
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
